import { div, h1, h2, span, button, img, select, option, a, fnlink, fnstate } from '@srfnstack/fntags'
import { fnfetch } from '../fetch.js'
import { showNotification } from '../state.js'

// Review queue for the mockup fidelity audit (cmd/fidelity-check -audit).
// One card per product: the reference art first, then each flagged mockup
// with its measurement in one line. Keep clears a mockup; Regenerate rejects
// every flagged mockup of the product and queues replacements.

// reasonFor turns a stored audit into the one short line the operator reads.
export const reasonFor = (audit) => {
  if (!audit) return ''
  if (audit.verdict === 'error') return `error: ${audit.error || 'unknown'}`
  const n = (v) => (typeof v === 'number' ? v.toFixed(2) : '?')
  const parts = (audit.flags || []).map(f => {
    if (f.startsWith('stretched')) return `stretched: panel ${n(audit.panel_aspect)} vs art ${n(audit.ref_aspect)}`
    if (f.startsWith('extended')) return `extended: best scale ${n(audit.best_scale)}`
    if (f.startsWith('cropped')) return `cropped: best scale ${n(audit.best_scale)}`
    if (f.startsWith('different image')) return `different image: best ncc ${n(audit.best_ncc)}`
    return f
  })
  if (audit.framed_verdict && audit.framed_verdict !== 'pass') {
    parts.push(`frame ${audit.framed_verdict} (${n(audit.framed_score)}): ${audit.framed_reasoning || ''}`)
  }
  if (parts.length === 0) return `pass (ncc ${n(audit.best_ncc)}, scale ${n(audit.best_scale)})`
  return parts.join('; ')
}

const TYPE_OPTIONS = [['all', 'Canvas and framed'], ['canvas', 'Canvas'], ['framed', 'Framed canvas']]
const VERDICT_OPTIONS = [['flag', 'Flagged'], ['all', 'Every audited']]

export const MockupAudit = () => {
  const type = fnstate('all')
  const verdict = fnstate('flag')
  const products = fnstate([], p => p.id)
  const loading = fnstate(true)

  const load = async () => {
    loading(true)
    try {
      const res = await fetch(`/api/mockup-audit?type=${type()}&verdict=${verdict()}`)
      products(res.ok ? await res.json() : [])
    } catch {
      products([])
    }
    loading(false)
  }
  type.subscribe(load)
  verdict.subscribe(load)
  load()

  const filter = (state, options) => select({
    class: 'select select-bordered select-sm',
    value: state.bindAttr(),
    onchange: e => state(e.target.value)
  }, ...options.map(([v, label]) => option({ value: v }, label)))

  const mockupTile = (mState) => {
    const m = mState()
    const keep = async () => {
      const res = await fnfetch(`/api/mockup-audit/${m.asset_id}/keep`, { method: 'POST' })
      if (res.ok) {
        mState.assign({ audit: { ...m.audit, verdict: 'kept' } })
      } else {
        showNotification('Failed to keep mockup', 'error')
      }
    }
    return div({ class: 'flex flex-col gap-1' },
      img({ src: m.url, alt: `mockup ${m.asset_id}`, class: 'w-full aspect-square object-cover rounded border border-base-300', loading: 'lazy' }),
      span({ class: 'text-xs leading-snug break-words' }, reasonFor(m.audit)),
      mState.bindAs(cur => cur.audit?.verdict === 'flag'
        ? button({ class: 'btn btn-xs btn-outline', onclick: keep }, 'Keep')
        : span({ class: 'badge badge-sm badge-ghost' }, cur.audit?.verdict || '')
      )
    )
  }

  const productCard = (pState) => {
    const p = pState()
    const mockups = fnstate(p.mockups || [], m => m.asset_id)
    const regen = fnstate({ busy: false, jobId: null })
    const flaggedCount = () => mockups().filter(m => m.audit?.verdict === 'flag').length
    const regenerate = async () => {
      regen({ busy: true, jobId: null })
      const res = await fnfetch(`/api/mockup-audit/${p.id}/regenerate`, { method: 'POST' })
      if (res.ok) {
        const data = await res.json()
        regen({ busy: false, jobId: data.job_id })
      } else {
        const e = await res.json().catch(() => ({}))
        showNotification(`Failed to regenerate: ${e.error || res.status}`, 'error')
        regen({ busy: false, jobId: null })
      }
    }
    return div({ class: 'card bg-base-100 border border-base-300 shadow-sm' },
      div({ class: 'card-body p-3 md:p-4 gap-3' },
        div({ class: 'flex flex-wrap items-center gap-2' },
          h2({ class: 'font-bold text-base grow min-w-0 truncate' }, p.title || `Product ${p.id}`),
          span({ class: 'badge badge-ghost badge-sm' }, p.product_type),
          span({ class: 'badge badge-outline badge-sm' }, p.status),
          // The framed sibling's chosen frame colour, so a flagged frame can
          // be judged against the colour the listing was pushed with.
          p.frame_color ? span({ class: 'text-xs opacity-70' }, `frame: ${p.frame_color}`) : '',
          fnlink({ to: `/product-review/${p.id}`, class: 'link link-primary text-sm' }, 'Review'),
          p.printify_url
            ? a({ href: p.printify_url, target: '_blank', rel: 'noopener', class: 'link text-sm' }, 'Printify')
            : '',
          p.etsy_url
            ? a({ href: p.etsy_url, target: '_blank', rel: 'noopener', class: 'link text-sm' }, 'Etsy')
            : ''
        ),
        div({ class: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2' },
          div({ class: 'flex flex-col gap-1' },
            p.reference?.url
              ? img({ src: p.reference.url, alt: 'reference art', class: 'w-full aspect-square object-contain bg-base-200 rounded border border-primary' })
              : div({ class: 'w-full aspect-square bg-base-200 rounded' }),
            span({ class: 'text-xs font-semibold' }, 'reference art')
          ),
          // Printify's own render of the product: for the framed sibling this
          // is the real frame profile the mockups must match.
          div({ class: 'flex flex-col gap-1' },
            p.printify_preview_url
              ? img({ src: p.printify_preview_url, alt: 'product (Printify render)', class: 'w-full aspect-square object-contain bg-base-200 rounded border border-base-300', loading: 'lazy' })
              : div({ class: 'w-full aspect-square bg-base-200 rounded flex items-center justify-center text-xs opacity-60 text-center p-2' }, 'no Printify render yet'),
            span({ class: 'text-xs font-semibold' }, 'product (Printify render)')
          ),
          // bindChildren owns its container's children, so the list gets a
          // display: contents wrapper and the tiles sit in the grid beside
          // the reference.
          mockups.bindChildren(div({ class: 'contents' }), mockupTile)
        ),
        div({ class: 'card-actions items-center gap-2' },
          regen.bindAs(r => r.jobId
            ? span({ class: 'text-sm' }, `Regenerating, job ${r.jobId}`)
            : button({
              class: 'btn btn-sm btn-warning',
              disabled: r.busy || flaggedCount() === 0,
              onclick: regenerate
            }, r.busy ? 'Queuing...' : `Regenerate ${flaggedCount()} flagged`)
          )
        )
      )
    )
  }

  return div({ class: 'p-4 md:p-6 flex flex-col gap-4' },
    div({ class: 'flex flex-wrap items-center gap-3' },
      h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Mockup Audit'),
      products.bindAs(list => span({ class: 'badge badge-ghost font-mono' }, `${list.length}`)),
      div({ class: 'flex gap-2 ml-auto' },
        filter(type, TYPE_OPTIONS),
        filter(verdict, VERDICT_OPTIONS)
      )
    ),
    loading.bindAs(l => l
      ? span({ class: 'loading loading-dots loading-md' })
      : products.bindAs(list => list.length === 0
        ? div({ class: 'opacity-60 text-sm' }, 'Nothing to review. Run fidelity-check -audit to measure the mockups.')
        : span()
      )
    ),
    products.bindChildren(div({ class: 'flex flex-col gap-4' }), productCard)
  )
}
